import { resolveWidgetTheme } from "@kuroo/ui/tokens";

const elementName = "kuroo-concierge";

type ChatRole = "user" | "assistant";
type ChatMessage = { id: string; role: ChatRole; parts: Array<{ type: "text"; text: string }> };
import {
  checkoutFromValue,
  eventFromValue,
  firstNumber,
  firstString,
  formatAppointmentRange,
  isConciergeProposalConfirmable,
  money,
  objectRecord,
  quoteFromValue,
  quoteOccurrencesFromSnapshot,
  randomId,
  textFromParts,
  type CheckoutSnapshot,
  type ConciergeAction,
  type ConciergeCustomer,
  type ConciergePaymentMethod,
  type QuoteOccurrence,
  type QuoteSnapshot
} from "./concierge-parsers";
import { renderConciergeShell } from "./concierge-view";
import { renderConciergeCheckout } from "./concierge-checkout-view";
export { isConciergeCheckoutRenderable, isConciergeProposalConfirmable } from "./concierge-parsers";
function safeSessionName(): string {
  const key = "kuroo-concierge-session";
  try {
    const current = window.sessionStorage.getItem(key);
    if (current && /^[a-z0-9-]{12,100}$/i.test(current)) return current;
    const next = randomId("session").replace(/[^a-z0-9-]/gi, "").slice(0, 80);
    window.sessionStorage.setItem(key, next);
    return next;
  } catch { return randomId("session"); }
}

export class KurooConciergeElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["api-base-url", "agent-base-url", "organization-id", "service-id", "provider-id", "location-id", "locale", "theme", "payment-method", "chatwoot-session-url", "chatwoot-account-id", "chatwoot-conversation-id", "chatwoot-inbox-id", "chatwoot-bridge-token"];
  }

  private readonly root = this.attachShadow({ mode: "open" });
  private socket?: WebSocket;
  private readonly sessionName = safeSessionName();
  private messages: ChatMessage[] = [];
  private activeAssistant = "";
  private activeRequestId?: string;
  private queuedText?: string;
  private queuedAction?: ConciergeAction;
  private pendingQuote?: QuoteSnapshot;
  private holdExpiresAt?: string;
  private pendingCheckout?: CheckoutSnapshot;
  private customer: ConciergeCustomer = { firstName: "", email: "" };
  private selectedOccurrenceSequence?: number;
  private paymentStatusRefreshing = false;
  private lastPaymentStatusCheckAt?: number;
  private paymentStatusMessage?: string;
  private readonly paymentStatusNotices: Array<{ id: string; text: string }> = [];
  private readonly paymentNoticeKeys = new Set<string>();
  private quoteExpiryTimer?: number;
  private paymentPollTimer?: number;
  private paymentPollAttempts = 0;
  private reconnectTimer?: number;
  private reconnectAttempts = 0;
  private resumeProbeId?: string;
  private readonly resumeAckedRequestIds = new Set<string>();
  private connectionWanted = false;
  private botPaused = false;
  private chatwootBootstrapPromise?: Promise<boolean>;
  private documentThemeObserver?: MutationObserver;

  connectedCallback(): void {
    this.observeDocumentTheme();
    this.render();
  }

  disconnectedCallback(): void {
    this.connectionWanted = false;
    this.documentThemeObserver?.disconnect();
    this.documentThemeObserver = undefined;
    this.resumeProbeId = undefined;
    this.resumeAckedRequestIds.clear();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.socket?.close(1000, "widget disconnected");
    this.socket = undefined;
    if (this.quoteExpiryTimer !== undefined) window.clearTimeout(this.quoteExpiryTimer);
    this.quoteExpiryTimer = undefined;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollTimer = undefined;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.render();
  }

  private observeDocumentTheme(): void {
    this.documentThemeObserver?.disconnect();
    this.documentThemeObserver = undefined;
    const configuredTheme = () => this.getAttribute("theme");
    const sync = (): void => {
      const theme = configuredTheme();
      if (theme === "dark" || theme === "light") return;
      const resolved = resolveWidgetTheme(theme);
      if (this.dataset.theme !== resolved) this.dataset.theme = resolved;
    };
    sync();
    if (typeof MutationObserver === "undefined" || typeof document === "undefined") return;
    this.documentThemeObserver = new MutationObserver(sync);
    this.documentThemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  }

  private currentLocale(): string {
    return this.getAttribute("locale") || document.documentElement.lang || navigator.language || "es-ES";
  }

  private agentSessionName(): string {
    const accountId = this.getAttribute("chatwoot-account-id")?.trim();
    const conversationId = this.getAttribute("chatwoot-conversation-id")?.trim();
    return accountId && conversationId ? `chatwoot:${accountId.slice(0, 80)}:${conversationId.slice(0, 100)}` : this.sessionName;
  }

  private chatwootSessionStorageKey(): string {
    const endpoint = this.getAttribute("chatwoot-session-url")?.trim() || "chatwoot";
    try { return `kuroo-chatwoot-session:${new URL(endpoint, document.baseURI).origin}`; }
    catch { return "kuroo-chatwoot-session"; }
  }

  private chatwootSessionId(): string {
    const key = "kuroo-chatwoot-client-id";
    try {
      const current = window.localStorage.getItem(key);
      if (current && /^[A-Za-z0-9_-]{16,100}$/u.test(current)) return current;
      const next = randomId("web").replace(/[^A-Za-z0-9_-]/gu, "");
      window.localStorage.setItem(key, next);
      return next;
    } catch { return randomId("web").replace(/[^A-Za-z0-9_-]/gu, ""); }
  }

  private applyChatwootSession(value: Record<string, unknown>): boolean {
    const accountId = typeof value.accountId === "string" ? value.accountId.trim() : "";
    const conversationId = typeof value.conversationId === "string" ? value.conversationId.trim() : "";
    const inboxId = typeof value.inboxId === "string" ? value.inboxId.trim() : "";
    const bridgeToken = typeof value.bridgeToken === "string" ? value.bridgeToken.trim() : "";
    const expiresAt = typeof value.expiresAt === "number" ? value.expiresAt : 0;
    if (!accountId || !conversationId || !inboxId || !bridgeToken || !/^\d+$/u.test(accountId) || !/^\d+$/u.test(conversationId) || !/^\d+$/u.test(inboxId) || expiresAt <= Date.now() + 30_000) return false;
    this.setAttribute("chatwoot-account-id", accountId);
    this.setAttribute("chatwoot-conversation-id", conversationId);
    this.setAttribute("chatwoot-inbox-id", inboxId);
    this.setAttribute("chatwoot-bridge-token", bridgeToken);
    return true;
  }

  private async ensureChatwootSession(): Promise<boolean> {
    const endpoint = this.getAttribute("chatwoot-session-url")?.trim();
    if (!endpoint) return true;
    if (this.getAttribute("chatwoot-account-id")?.trim() && this.getAttribute("chatwoot-conversation-id")?.trim() && this.getAttribute("chatwoot-bridge-token")?.trim()) return true;
    if (this.chatwootBootstrapPromise) return this.chatwootBootstrapPromise;
    this.chatwootBootstrapPromise = (async () => {
      const storageKey = this.chatwootSessionStorageKey();
      try {
        const cached = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as unknown;
        if (cached && typeof cached === "object" && !Array.isArray(cached) && this.applyChatwootSession(cached as Record<string, unknown>)) return true;
      } catch { /* ignore an invalid browser cache */ }
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          credentials: "omit",
          cache: "no-store",
          body: JSON.stringify({ sessionId: this.chatwootSessionId() })
        });
        const value = await response.json().catch(() => undefined) as unknown;
        if (!response.ok || !value || typeof value !== "object" || Array.isArray(value) || !this.applyChatwootSession(value as Record<string, unknown>)) throw new Error("CHATWOOT_SESSION_FAILED");
        try { window.localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* storage is optional */ }
        return true;
      } catch {
        // The concierge remains usable if Chatwoot is temporarily unavailable;
        // only the transcript mirror is degraded for this browser session.
        this.setStatus("Chatwoot no está disponible; seguimos con el concierge.", true);
        return true;
      }
    })();
    return this.chatwootBootstrapPromise;
  }

  private agentUrl(): string | undefined {
    const base = this.getAttribute("agent-base-url")?.trim();
    if (!base) return undefined;
    try {
      const url = new URL(base, document.baseURI);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = `${url.pathname.replace(/\/$/, "")}/agents/concierge-agent/${encodeURIComponent(this.agentSessionName())}`;
      const bridgeToken = this.getAttribute("chatwoot-bridge-token")?.trim();
      if (bridgeToken && this.getAttribute("chatwoot-account-id")?.trim() && this.getAttribute("chatwoot-conversation-id")?.trim()) {
        url.searchParams.set("bridgeToken", bridgeToken);
      }
      return url.toString();
    } catch { return undefined; }
  }

  private context(): Record<string, unknown> {
    const value: Record<string, unknown> = { locale: this.currentLocale() };
    const organizationId = this.getAttribute("organization-id")?.trim();
    if (organizationId) value.organizationId = organizationId;
    for (const name of ["service-id", "provider-id", "location-id"] as const) {
      const attribute = name.replaceAll("-", "");
      const current = this.getAttribute(name);
      if (current) value[attribute === "serviceid" ? "serviceId" : attribute === "providerid" ? "providerId" : "locationId"] = current;
    }
    const paymentMethod = this.getAttribute("payment-method")?.trim();
    if (paymentMethod && ["mercadopago", "stripe", "paypal"].includes(paymentMethod)) value.paymentMethod = paymentMethod;
    const chatwootAccountId = this.getAttribute("chatwoot-account-id")?.trim();
    const chatwootConversationId = this.getAttribute("chatwoot-conversation-id")?.trim();
    const chatwootInboxId = this.getAttribute("chatwoot-inbox-id")?.trim();
    const chatwootBridgeToken = this.getAttribute("chatwoot-bridge-token")?.trim();
    if (chatwootAccountId) value.chatwootAccountId = chatwootAccountId;
    if (chatwootConversationId) value.chatwootConversationId = chatwootConversationId;
    if (chatwootInboxId) value.chatwootInboxId = chatwootInboxId;
    if (chatwootBridgeToken) value.chatwootBridgeToken = chatwootBridgeToken;
    return value;
  }

  private selectedPaymentMethod(): ConciergePaymentMethod | undefined {
    const value = this.getAttribute("payment-method")?.trim();
    return value === "mercadopago" || value === "stripe" || value === "paypal" ? value : undefined;
  }

  private render(): void {
    const configuredTheme = this.getAttribute("theme") || "default";
    const theme = resolveWidgetTheme(configuredTheme);
    this.dataset.theme = theme;
    const hasUserMessage = this.messages.some((message) => message.role === "user");
    const inputPlaceholder = hasUserMessage ? "" : "Ej.: necesito un turno esta semana…";
    this.root.innerHTML = renderConciergeShell({ configuredTheme, theme, inputPlaceholder });
    const form = this.root.querySelector("[data-form]") as HTMLFormElement | null;
    const input = this.root.querySelector("[data-input]") as HTMLTextAreaElement | null;
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      const text = input?.value.trim() ?? "";
      if (input) input.value = "";
      if (text) void this.send(text);
    });
    input?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form?.requestSubmit();
      }
    });
    this.root.querySelector("[data-confirm]")?.addEventListener("click", () => {
      if (!this.pendingQuote || !this.proposalIsConfirmable()) return;
      void this.sendAction({
        type: "confirm-proposal",
        proposalId: this.pendingQuote.proposalId ?? this.pendingQuote.id,
        customer: {
          firstName: this.customer.firstName.trim(),
          email: this.customer.email.trim(),
          ...(this.customer.lastName?.trim() ? { lastName: this.customer.lastName.trim() } : {}),
          ...(this.customer.phone?.trim() ? { phone: this.customer.phone.trim() } : {}),
          ...(this.pendingQuote.consentRequired ? { consent: this.customer.consent === true } : {})
        },
        // The explicit button accepts the complete server quote, including
        // any Kuroo adjustment. There is no second checkbox to block it.
        adjustmentAccepted: true,
        ...(this.selectedPaymentMethod() ? { paymentMethod: this.selectedPaymentMethod() } : {})
      });
    });
    this.applyInputState();
    this.renderMessages();
    this.renderQuote();
    this.renderCheckout();
  }

  private setStatus(message: string, error = false, busy = false): void {
    const status = this.root.querySelector("[data-status]");
    if (status) {
      status.textContent = message;
      status.setAttribute("aria-live", error ? "assertive" : "polite");
      status.classList.toggle("busy", busy);
      status.setAttribute("aria-busy", busy ? "true" : "false");
    }
  }

  private applyInputState(): void {
    const input = this.root.querySelector("[data-input]") as HTMLTextAreaElement | null;
    const submit = this.root.querySelector("[data-form] button") as HTMLButtonElement | null;
    const disabled = this.botPaused || Boolean(this.activeRequestId);
    if (input) input.disabled = disabled;
    if (submit) submit.disabled = disabled;
  }

  private clearInputPlaceholder(): void {
    const input = this.root.querySelector("[data-input]") as HTMLTextAreaElement | null;
    if (input) input.placeholder = "";
  }

  private renderMessages(): void {
    const container = this.root.querySelector("[data-messages]");
    if (!container) return;
    container.replaceChildren();
    for (const message of this.messages) {
      const item = document.createElement("div");
      item.className = `message ${message.role}`;
      item.textContent = textFromParts(message.parts);
      container.append(item);
    }
    if (this.activeAssistant) {
      const item = document.createElement("div");
      item.className = "message assistant";
      item.textContent = this.activeAssistant;
      container.append(item);
    }
    for (const notice of this.paymentStatusNotices) {
      const item = document.createElement("div");
      item.className = "message system";
      item.dataset.noticeId = notice.id;
      item.textContent = notice.text;
      container.append(item);
    }
    container.scrollTop = container.scrollHeight;
  }

  private quoteOccurrences(quote: QuoteSnapshot): QuoteOccurrence[] {
    return quoteOccurrencesFromSnapshot(quote);
  }

  private proposalIsConfirmable(): boolean {
    return isConciergeProposalConfirmable(this.pendingQuote, this.customer);
  }

  private updateConfirmState(): void {
    const confirm = this.root.querySelector("[data-confirm]") as HTMLButtonElement | null;
    if (confirm) confirm.disabled = this.botPaused || Boolean(this.activeRequestId) || !this.proposalIsConfirmable();
  }

  private proposalDecisionOccurrences(): Array<{ sequence: number; include: boolean; quoteId?: string; holdId?: string; slotKey?: string; startAt?: string; endAt?: string; providerId?: string; locationId?: string }> {
    return this.quoteOccurrences(this.pendingQuote ?? { id: "", totalCents: 0, expiresAt: "" }).map((occurrence) => ({
      sequence: occurrence.sequence,
      include: !["excluded", "cancelled"].includes(occurrence.status ?? ""),
      ...(occurrence.quoteId ? { quoteId: occurrence.quoteId } : {}),
      ...(occurrence.holdId ? { holdId: occurrence.holdId } : {}),
      ...(occurrence.slotKey ? { slotKey: occurrence.slotKey } : {}),
      ...(occurrence.startAt ? { startAt: occurrence.startAt } : {}),
      ...(occurrence.endAt ? { endAt: occurrence.endAt } : {}),
      ...(occurrence.providerId ? { providerId: occurrence.providerId } : {}),
      ...(occurrence.locationId ? { locationId: occurrence.locationId } : {})
    }));
  }

  private updateProposalDecision(sequence: number, change: { include?: boolean }): void {
    const quote = this.pendingQuote;
    const proposalId = quote?.proposalId ?? quote?.id;
    if (!quote || !proposalId || !quote.occurrences?.length || (quote.kind ?? quote.targetKind) !== "series" || this.activeRequestId) return;
    this.pendingQuote = {
      ...quote,
      occurrences: quote.occurrences.map((occurrence) => {
        if (occurrence.sequence !== sequence) return occurrence;
        const nextStatus = change.include === false ? "excluded" : change.include === true && ["excluded", "cancelled"].includes(occurrence.status ?? "") ? "proposed" : occurrence.status;
        return { ...occurrence, ...(nextStatus ? { status: nextStatus } : {}) };
      })
    };
    this.renderQuote();
    void this.sendAction({ type: "update-proposal", proposalId, occurrences: this.proposalDecisionOccurrences() });
  }

  private appendQuoteRow(container: HTMLElement, label: string, value: string | undefined): void {
    if (!value) return;
    const row = document.createElement("div");
    row.className = "quote-row";
    const name = document.createElement("span");
    name.textContent = label;
    const content = document.createElement("span");
    content.textContent = value;
    row.append(name, content);
    container.append(row);
  }

  private appendQuoteLines(card: HTMLElement, lines: unknown[] | undefined, currency: string | undefined, totalCents?: number): void {
    if (!lines?.length) return;
    const normalized = lines.flatMap((raw) => {
      const line = objectRecord(raw);
      if (!line) return [];
      const label = firstString([line], "label", "name") ?? "Concepto";
      const total = firstNumber([line], "totalCents", "total_cents");
      const quantity = firstNumber([line], "quantity", "qty");
      return [{ label, total, quantity }];
    });
    // A single service line equal to the proposal total is already conveyed
    // by the compact total in the card and would only repeat the same price.
    if (normalized.length === 1 && totalCents !== undefined && normalized[0].total === totalCents) return;
    const list = document.createElement("div");
    list.className = "quote-lines";
    for (const line of normalized) {
      const item = document.createElement("div");
      item.textContent = `${line.quantity && line.quantity > 1 ? `${line.quantity} × ` : ""}${line.label}${line.total !== undefined ? `: ${money(line.total, currency, this.currentLocale())}` : ""}`;
      list.append(item);
    }
    if (list.childElementCount) card.append(list);
  }

  private renderOccurrence(container: HTMLElement, occurrence: QuoteOccurrence, totalOccurrences: number): void {
    const item = document.createElement("div");
    item.className = `proposal-occurrence ${occurrence.status ?? ""}`;
    const heading = document.createElement("div");
    heading.className = "occurrence-heading";
    const title = document.createElement("strong");
    title.textContent = totalOccurrences > 1 ? `Cita ${occurrence.sequence + 1}` : "Detalle de la cita";
    heading.append(title);
    if (occurrence.totalCents !== undefined) {
      const total = document.createElement("span");
      total.className = "occurrence-total";
      total.textContent = money(occurrence.totalCents, occurrence.currency ?? this.pendingQuote?.currency, this.currentLocale());
      heading.append(total);
    }
    item.append(heading);
    const details = document.createElement("div");
    details.className = "quote-details";
    if (totalOccurrences === 1) this.appendQuoteRow(details, "Servicio", occurrence.serviceName);
    this.appendQuoteRow(details, "Fecha", formatAppointmentRange(occurrence, this.currentLocale()));
    this.appendQuoteRow(details, "Profesional", occurrence.providerName);
    this.appendQuoteRow(details, "Ubicación", occurrence.locationName ?? (occurrence.locationId ? undefined : "Videollamada"));
    this.appendQuoteRow(details, "Duración", occurrence.durationMinutes ? `${occurrence.durationMinutes} min` : undefined);
    this.appendQuoteRow(details, "Personas", occurrence.persons ? String(occurrence.persons) : undefined);
    if (occurrence.status === "unavailable") this.appendQuoteRow(details, "Estado", "No disponible; elegí otra opción o excluí esta fecha.");
    if (occurrence.adjusted) this.appendQuoteRow(details, "Ajuste", occurrence.adjustmentReason ?? "Ajuste aplicado por Kuroo.");
    if (details.childElementCount) item.append(details);
    if (totalOccurrences > 1 && ["unavailable", "excluded", "cancelled"].includes(occurrence.status ?? "")) {
      const decision = document.createElement("label");
      const include = document.createElement("input");
      include.type = "checkbox";
      include.checked = !["excluded", "cancelled"].includes(occurrence.status ?? "");
      include.disabled = Boolean(this.activeRequestId);
      include.addEventListener("change", () => this.updateProposalDecision(occurrence.sequence, { include: include.checked }));
      decision.append(include, document.createTextNode(" Incluir esta fecha en la serie"));
      item.append(decision);
    }
    container.append(item);
  }

  private renderOccurrenceTabs(container: HTMLElement, occurrences: QuoteOccurrence[]): void {
    const selected = occurrences.find((occurrence) => occurrence.sequence === this.selectedOccurrenceSequence) ?? occurrences[0];
    if (!selected) return;
    this.selectedOccurrenceSequence = selected.sequence;
    const tabs = document.createElement("div");
    tabs.className = "proposal-tabs";
    tabs.setAttribute("role", "tablist");
    tabs.setAttribute("aria-label", "Citas de la propuesta");
    const panelId = "kuroo-concierge-occurrence-panel";
    for (let index = 0; index < occurrences.length; index += 1) {
      const occurrence = occurrences[index];
      const button = document.createElement("button");
      button.type = "button";
      button.className = "proposal-tab";
      button.dataset.occurrenceSequence = String(occurrence.sequence);
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", occurrence.sequence === selected.sequence ? "true" : "false");
      button.setAttribute("aria-controls", panelId);
      button.tabIndex = occurrence.sequence === selected.sequence ? 0 : -1;
      const date = occurrence.startAt ? (() => {
        try {
          return new Intl.DateTimeFormat(this.currentLocale(), { day: "numeric", month: "short", ...(occurrence.timezone ? { timeZone: occurrence.timezone } : {}) }).format(new Date(occurrence.startAt));
        } catch { return undefined; }
      })() : undefined;
      button.textContent = `Cita ${occurrence.sequence + 1}${date ? ` · ${date}` : ""}`;
      button.addEventListener("click", () => {
        this.selectedOccurrenceSequence = occurrence.sequence;
        this.renderQuote();
      });
      button.addEventListener("keydown", (event) => {
        if (!(event.key === "ArrowRight" || event.key === "ArrowDown" || event.key === "ArrowLeft" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End")) return;
        event.preventDefault();
        const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? occurrences.length - 1 : (index + (event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1) + occurrences.length) % occurrences.length;
        this.selectedOccurrenceSequence = occurrences[nextIndex].sequence;
        this.renderQuote();
        const next = this.root.querySelector(`[data-occurrence-sequence="${occurrences[nextIndex].sequence}"]`) as HTMLButtonElement | null;
        next?.focus();
      });
      tabs.append(button);
    }
    const panel = document.createElement("div");
    panel.className = "proposal-tabpanel";
    panel.id = panelId;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-label", `Detalle de la cita ${selected.sequence + 1}`);
    this.renderOccurrence(panel, selected, occurrences.length);
    container.append(tabs, panel);
  }

  private renderCustomerForm(card: HTMLElement): void {
    const form = document.createElement("div");
    form.className = "customer-form";
    const heading = document.createElement("strong");
    heading.textContent = "Datos para reservar";
    form.append(heading);
    const grid = document.createElement("div");
    grid.className = "customer-grid";
    const addInput = (key: "firstName" | "email" | "lastName" | "phone", labelText: string, type: string, required: boolean): void => {
      const label = document.createElement("label");
      label.textContent = `${labelText}${required ? " *" : ""}`;
      const input = document.createElement("input");
      input.type = type;
      input.required = required;
      input.value = this.customer[key] ?? "";
      input.autocomplete = key === "firstName" ? "given-name" : key === "lastName" ? "family-name" : key === "email" ? "email" : "tel";
      input.addEventListener("input", () => {
        this.customer = { ...this.customer, [key]: input.value };
        this.updateConfirmState();
      });
      label.append(input);
      grid.append(label);
    };
    addInput("firstName", "Nombre", "text", true);
    addInput("email", "Email", "email", true);
    addInput("lastName", "Apellido", "text", false);
    addInput("phone", "Teléfono", "tel", false);
    form.append(grid);
    if (this.pendingQuote?.consentRequired) {
      const consentLabel = document.createElement("label");
      const consent = document.createElement("input");
      consent.type = "checkbox";
      consent.checked = this.customer.consent === true;
      consent.addEventListener("change", () => {
        this.customer = { ...this.customer, consent: consent.checked };
        this.updateConfirmState();
      });
      consentLabel.append(consent, document.createTextNode(" Acepto el tratamiento de mis datos para gestionar la reserva."));
      form.append(consentLabel);
    }
    card.append(form);
  }

  private scheduleQuoteExpiry(quote: QuoteSnapshot, expiry: number): void {
    if (!Number.isFinite(expiry) || expiry <= Date.now()) return;
    const proposalId = quote.proposalId ?? quote.id;
    const delay = Math.min(Math.max(expiry - Date.now() + 50, 50), 2_147_483_647);
    this.quoteExpiryTimer = window.setTimeout(() => {
      this.quoteExpiryTimer = undefined;
      const current = this.pendingQuote;
      const currentId = current?.proposalId ?? current?.id;
      if (!current || currentId !== proposalId || Date.parse(current.expiresAt) > Date.now()) return;
      this.pendingQuote = undefined;
      this.renderQuote();
      this.setStatus("La propuesta expiró. Pedí una nueva disponibilidad para continuar.", true);
    }, delay);
  }

  private mergeProposalDecisions(quote: QuoteSnapshot): QuoteSnapshot {
    const previousBySequence = new Map((this.pendingQuote?.occurrences ?? []).map((occurrence) => [occurrence.sequence, occurrence]));
    if (!quote.occurrences?.length || !previousBySequence.size) return quote;
    return {
      ...quote,
      occurrences: quote.occurrences.map((occurrence) => {
        const previous = previousBySequence.get(occurrence.sequence);
        return previous && occurrence.adjustmentAccepted === undefined && previous.adjustmentAccepted !== undefined
          ? { ...occurrence, adjustmentAccepted: previous.adjustmentAccepted }
          : occurrence;
      })
    };
  }

  private renderQuote(): void {
    const card = this.root.querySelector("[data-quote]") as HTMLElement | null;
    const confirm = this.root.querySelector("[data-confirm]") as HTMLButtonElement | null;
    if (!card || !confirm) return;
    if (this.quoteExpiryTimer !== undefined) window.clearTimeout(this.quoteExpiryTimer);
    this.quoteExpiryTimer = undefined;
    if (!this.pendingQuote) { card.hidden = true; confirm.hidden = true; confirm.disabled = true; return; }
    const quote = this.pendingQuote;
    const expiry = Date.parse(quote.expiresAt);
    if (Number.isFinite(expiry) && expiry <= Date.now()) {
      this.pendingQuote = undefined;
      card.hidden = true;
      confirm.hidden = true;
      confirm.disabled = true;
      if (!this.activeRequestId) this.setStatus("La propuesta expiró. Pedí una nueva disponibilidad para continuar.", true);
      return;
    }
    card.hidden = false;
    card.replaceChildren();
    const strong = document.createElement("strong");
    strong.textContent = `Propuesta: ${money(quote.totalCents, quote.currency, this.currentLocale())}`;
    const small = document.createElement("small");
    small.textContent = Number.isFinite(expiry) ? `Válida hasta ${new Date(expiry).toLocaleString(this.currentLocale())}.` : "Propuesta calculada por Kuroo.";
    card.append(strong, small);
    const occurrences = this.quoteOccurrences(quote);
    if (occurrences.length) {
      const list = document.createElement("div");
      list.className = "proposal-occurrences";
      if (occurrences.length > 1) this.renderOccurrenceTabs(list, occurrences);
      else this.renderOccurrence(list, occurrences[0], occurrences.length);
      card.append(list);
    }
    this.appendQuoteLines(card, quote.lines, quote.currency, quote.totalCents);
    const ruleText = quote.appliedRules?.map((rule) => rule.reason ?? rule.label).filter((value): value is string => Boolean(value)).join(" · ");
    if (ruleText) {
      const rules = document.createElement("small");
      rules.textContent = `Ajustes de Kuroo: ${ruleText}`;
      card.append(rules);
    }
    this.renderCustomerForm(card);
    confirm.hidden = false;
    this.updateConfirmState();
    this.scheduleQuoteExpiry(quote, expiry);
    this.dispatchEvent(new CustomEvent("kuroo:quote", { bubbles: true, composed: true, detail: this.pendingQuote }));
  }

  private paymentStatusUrl(paymentId: string): string | undefined {
    const base = this.getAttribute("api-base-url")?.trim();
    if (!base) return undefined;
    const normalized = base.replace(/\/$/u, "");
    const api = normalized.endsWith("/api/v1") ? normalized : `${normalized}/api/v1`;
    return `${api}/payments/${encodeURIComponent(paymentId)}/status`;
  }

  private paymentStatusRefreshUrl(paymentId: string): string | undefined {
    const url = this.paymentStatusUrl(paymentId);
    return url ? `${url}/refresh` : undefined;
  }

  private paymentIsFinished(checkout: CheckoutSnapshot): boolean {
    return ["paid", "failed", "cancelled", "expired", "refunded", "partially_refunded"].includes(checkout.status ?? "")
      || checkout.bookingStatus === "confirmed"
      || checkout.eventOrderStatus === "confirmed"
      || checkout.recurringPaymentState === "paid";
  }

  private appendPaymentStatusNotice(checkout: CheckoutSnapshot): void {
    if (!this.paymentIsFinished(checkout)) return;
    const key = `${checkout.paymentId ?? checkout.bookingId ?? checkout.eventOrderId ?? "payment"}:${checkout.status ?? ""}:${checkout.bookingStatus ?? ""}:${checkout.eventOrderStatus ?? ""}:${checkout.recurringPaymentState ?? ""}`;
    if (this.paymentNoticeKeys.has(key)) return;
    this.paymentNoticeKeys.add(key);
    const confirmed = checkout.bookingStatus === "confirmed" || checkout.eventOrderStatus === "confirmed" || checkout.recurringPaymentState === "paid";
    const text = confirmed
      ? "Pago aprobado y reserva confirmada en Kuroo. Te enviaremos los detalles por los canales configurados."
      : checkout.status === "paid"
        ? "Pago aprobado. Kuroo está terminando de confirmar la reserva; el webhook del proveedor es la confirmación definitiva."
        : checkout.status === "failed"
          ? "El proveedor rechazó el pago. Podés reintentarlo desde el checkout seguro."
          : "El checkout fue cancelado o expiró. Si querés, puedo generar una nueva propuesta.";
    this.paymentStatusNotices.push({ id: randomId("payment-status"), text });
    this.renderMessages();
    this.dispatchEvent(new CustomEvent("kuroo:payment-notice", { bubbles: true, composed: true, detail: { checkout, text } }));
  }

  private paymentStatusLabel(status: string | undefined): string {
    switch (status) {
      case "paid": return "Pago aprobado; Kuroo está terminando de confirmar la reserva.";
      case "failed": return "El pago fue rechazado. Podés reintentar desde el checkout.";
      case "cancelled":
      case "expired": return "El checkout expiró o fue cancelado.";
      case "refunded":
      case "partially_refunded": return "El pago fue reembolsado.";
      case "authorized": return "Pago autorizado; esperando la confirmación del proveedor.";
      default: return "Pago pendiente; esta tarjeta se actualizará automáticamente.";
    }
  }

  private renderCheckout(): void {
    renderConciergeCheckout({
      card: this.root.querySelector("[data-checkout]") as HTMLElement | null,
      checkout: this.pendingCheckout,
      paymentStatusRefreshing: this.paymentStatusRefreshing,
      activeRequestId: this.activeRequestId,
      paymentStatusMessage: this.paymentStatusMessage,
      lastPaymentStatusCheckAt: this.lastPaymentStatusCheckAt,
      locale: this.currentLocale(),
      paymentIsFinished: (checkout) => this.paymentIsFinished(checkout),
      paymentStatusLabel: (status) => this.paymentStatusLabel(status),
      onRefresh: () => { void this.refreshCheckoutStatus(true); }
    });
  }

  private startCheckoutPolling(): void {
    if (!this.pendingCheckout?.paymentId || !this.paymentStatusUrl(this.pendingCheckout.paymentId)) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshCheckoutStatus(), 5_000);
    void this.refreshCheckoutStatus();
  }

  private async refreshCheckoutStatus(manual = false): Promise<void> {
    if (this.paymentStatusRefreshing) return;
    const checkoutBefore = this.pendingCheckout;
    const paymentId = checkoutBefore?.paymentId;
    const refreshUrl = paymentId ? this.paymentStatusRefreshUrl(paymentId) : undefined;
    if (!refreshUrl || (this.paymentPollTimer === undefined && !manual)) return;
    if (!checkoutBefore?.statusToken) {
      if (manual) {
        this.paymentStatusMessage = "Este checkout no tiene una capacidad de consulta válida. Generá un nuevo checkout para continuar.";
        this.renderCheckout();
      }
      return;
    }
    this.paymentStatusRefreshing = true;
    if (manual) {
      // Manual refresh is always available after automatic polling stops.
      this.paymentPollAttempts = 0;
      this.paymentStatusRefreshing = true;
      this.paymentStatusMessage = "Consultando el estado real en el proveedor…";
      this.renderCheckout();
      this.setStatus("Consultando el estado del pago…", false, true);
    }
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      this.paymentStatusRefreshing = false;
      if (manual) {
        this.paymentStatusMessage = "La consulta automática terminó; podés volver a consultar manualmente.";
        this.renderCheckout();
      }
      return;
    }
    this.paymentPollAttempts += 1;
    try {
      const response = await fetch(refreshUrl, {
        method: "POST",
        credentials: "include",
        headers: { accept: "application/json", "X-Kuroo-Payment-Capability": checkoutBefore.statusToken }
      });
      const payload = await response.json().catch(() => undefined) as Record<string, unknown> | undefined;
      const data = payload && typeof payload.data === "object" && payload.data !== null ? payload.data as Record<string, unknown> : payload;
      if (!response.ok || !data) throw new Error("PAYMENT_STATUS_FAILED");
      this.pendingCheckout = {
        ...this.pendingCheckout,
        status: typeof data.status === "string" ? data.status : this.pendingCheckout?.status,
        statusDetail: typeof data.statusDetail === "string" ? data.statusDetail : this.pendingCheckout?.statusDetail,
        bookingStatus: typeof data.bookingStatus === "string" ? data.bookingStatus : this.pendingCheckout?.bookingStatus,
        eventOrderStatus: typeof data.eventOrderStatus === "string" ? data.eventOrderStatus : this.pendingCheckout?.eventOrderStatus,
        recurringSeriesStatus: typeof data.recurringSeriesStatus === "string" ? data.recurringSeriesStatus : this.pendingCheckout?.recurringSeriesStatus,
        recurringPaymentState: typeof data.recurringPaymentState === "string" ? data.recurringPaymentState : this.pendingCheckout?.recurringPaymentState
      };
      this.lastPaymentStatusCheckAt = Date.now();
      this.paymentStatusMessage = this.paymentIsFinished(this.pendingCheckout)
        ? "Estado confirmado por Kuroo."
        : "Todavía no recibimos confirmación del proveedor; seguiremos consultando automáticamente.";
      this.appendPaymentStatusNotice(this.pendingCheckout);
      this.renderCheckout();
      this.dispatchEvent(new CustomEvent("kuroo:payment-status", { bubbles: true, composed: true, detail: this.pendingCheckout }));
      if (this.paymentIsFinished(this.pendingCheckout)) {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
      }
    } catch {
      if (manual) {
        this.paymentStatusMessage = "No pudimos consultar el estado del proveedor. Reintentá en unos segundos.";
        this.renderCheckout();
        this.setStatus("No pudimos consultar el estado del pago. Reintentá en unos segundos.", true);
      }
    } finally {
      this.paymentStatusRefreshing = false;
      this.renderCheckout();
    }
  }

  private scheduleReconnect(): void {
    if (!this.isConnected || !this.connectionWanted || this.reconnectTimer !== undefined) return;
    const attempt = this.reconnectAttempts;
    this.reconnectAttempts += 1;
    const delay = Math.min(10_000, 1_000 * (2 ** Math.min(attempt, 4)));
    this.setStatus(this.activeRequestId ? "Reconectando y retomando la operación…" : "Reconectando…", false, true);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  private async connect(): Promise<void> {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    if (!(await this.ensureChatwootSession())) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    const url = this.agentUrl();
    if (!url) { this.setStatus("El concierge no está configurado temporalmente. Reintentá más tarde.", true); return; }
    try {
      this.socket = new WebSocket(url);
      this.socket.addEventListener("open", () => {
        this.reconnectAttempts = 0;
        if (this.activeRequestId) {
          this.setStatus("Retomando la operación…", false, true);
          const probeId = randomId("resume");
          this.resumeProbeId = probeId;
          this.socket?.send(JSON.stringify({ type: "cf_agent_stream_resume_request", probeId }));
          return;
        }
        this.setStatus("Listo para ayudarte.");
        const queuedText = this.queuedText;
        const queuedAction = this.queuedAction;
        this.queuedText = undefined;
        this.queuedAction = undefined;
        if (queuedAction) void this.sendAction(queuedAction);
        else if (queuedText) void this.send(queuedText);
      });
      this.socket.addEventListener("message", (event) => this.receive(event.data));
      this.socket.addEventListener("error", () => {
        this.setStatus("No pudimos conectar el concierge. Reintentá en unos segundos.", true);
        this.dispatchEvent(new CustomEvent("kuroo:error", { bubbles: true, composed: true, detail: { stage: "connection" } }));
      });
      this.socket.addEventListener("close", () => {
        this.socket = undefined;
        this.resumeAckedRequestIds.clear();
        if (this.activeRequestId) {
          this.setStatus("La conexión se interrumpió; podés reintentar sin perder la propuesta.", true);
        }
        this.scheduleReconnect();
      });
    } catch {
      this.setStatus("No pudimos iniciar el concierge. Reintentá en unos segundos.", true);
      this.scheduleReconnect();
    }
  }

  private async send(text: string): Promise<void> {
    this.connectionWanted = true;
    if (this.botPaused) {
      this.setStatus("Una persona está atendiendo esta conversación. El concierge está pausado.", true);
      return;
    }
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.queuedText = text;
      this.queuedAction = undefined;
      this.clearInputPlaceholder();
      this.setStatus("Conectando…", false, true);
      this.connect();
      return;
    }
    const user: ChatMessage = { id: randomId("message"), role: "user", parts: [{ type: "text", text }] };
    this.messages.push(user);
    this.dispatchEvent(new CustomEvent("kuroo:message", { bubbles: true, composed: true, detail: { role: "user", text } }));
    await this.sendRequest();
  }

  private async sendAction(action: ConciergeAction): Promise<void> {
    this.connectionWanted = true;
    if (this.botPaused) {
      this.setStatus("Una persona está atendiendo esta conversación. El concierge está pausado.", true);
      return;
    }
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.queuedAction = action;
      this.queuedText = undefined;
      this.setStatus("Conectando…", false, true);
      this.connect();
      return;
    }
    await this.sendRequest(action);
  }

  private async sendRequest(action?: ConciergeAction): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN || this.botPaused) return;
    this.clearInputPlaceholder();
    this.activeAssistant = "";
    this.activeRequestId = randomId("request");
    this.applyInputState();
    this.renderMessages();
    this.renderQuote();
    this.setStatus(action ? "Creando checkout seguro…" : "Pensando…", false, true);
    const body = JSON.stringify({
      messages: this.messages,
      ...this.context(),
      ...(action ? { kurooAction: action } : {})
    });
    socket.send(JSON.stringify({ id: this.activeRequestId, init: { method: "POST", body }, type: "cf_agent_use_chat_request" }));
  }

  private consumeUiEvent(value: unknown): void {
    if (!value || typeof value !== "object") return;
    const record = value as Record<string, unknown>;
    const structuredType = typeof record.type === "string" ? record.type : undefined;
    const payload = record.payload && typeof record.payload === "object" && !Array.isArray(record.payload) ? record.payload : value;
    if (structuredType === "quote") {
      const quote = quoteFromValue(payload);
      if (quote) { this.pendingQuote = this.mergeProposalDecisions(quote); this.renderQuote(); }
      return;
    }
    if (structuredType === "hold") {
      const hold = payload as Record<string, unknown>;
      if (typeof hold.expiresAt === "string") {
        this.holdExpiresAt = hold.expiresAt;
        const quoteId = typeof hold.quoteId === "string" ? hold.quoteId : undefined;
        const sequence = typeof hold.sequence === "number" ? hold.sequence : undefined;
        if (this.pendingQuote) {
          const occurrences = this.quoteOccurrences(this.pendingQuote);
          this.pendingQuote = {
            ...this.pendingQuote,
            occurrences: occurrences.map((occurrence) => occurrence.sequence === sequence || (quoteId && (occurrence.quoteId === quoteId || this.pendingQuote?.id === quoteId))
              ? { ...occurrence, holdId: typeof hold.holdId === "string" ? hold.holdId : occurrence.holdId, status: "held" }
              : occurrence)
          };
        }
        this.renderQuote();
        this.dispatchEvent(new CustomEvent("kuroo:hold", { bubbles: true, composed: true, detail: hold }));
      }
      return;
    }
    if (structuredType === "checkout") {
      const checkout = checkoutFromValue(value);
      if (checkout) {
        const previousPaymentId = this.pendingCheckout?.paymentId;
        this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
        if (previousPaymentId && checkout.paymentId && previousPaymentId !== checkout.paymentId) {
          this.paymentStatusMessage = undefined;
          this.lastPaymentStatusCheckAt = undefined;
        }
        this.appendPaymentStatusNotice(this.pendingCheckout);
        this.renderCheckout();
        this.dispatchEvent(new CustomEvent("kuroo:checkout", { bubbles: true, composed: true, detail: this.pendingCheckout }));
        this.startCheckoutPolling();
      }
      return;
    }
    if (structuredType === "payment_status") {
      const status = payload as Record<string, unknown>;
      if (this.pendingCheckout && typeof status.status === "string") {
        this.pendingCheckout = {
          ...this.pendingCheckout,
          status: status.status,
          statusDetail: typeof status.statusDetail === "string" ? status.statusDetail : this.pendingCheckout.statusDetail,
          bookingStatus: typeof status.bookingStatus === "string" ? status.bookingStatus : this.pendingCheckout.bookingStatus,
          eventOrderStatus: typeof status.eventOrderStatus === "string" ? status.eventOrderStatus : this.pendingCheckout.eventOrderStatus,
          recurringSeriesStatus: typeof status.recurringSeriesStatus === "string" ? status.recurringSeriesStatus : this.pendingCheckout.recurringSeriesStatus,
          recurringPaymentState: typeof status.recurringPaymentState === "string" ? status.recurringPaymentState : this.pendingCheckout.recurringPaymentState
        };
        this.appendPaymentStatusNotice(this.pendingCheckout);
        this.renderCheckout();
      }
      return;
    }
    if (structuredType === "fallback") {
      this.setStatus("La reserva necesita una decisión adicional. Revisá la propuesta y respondé para continuar.", true);
      return;
    }
    const event = eventFromValue(value);
    if (!event) return;
    if (event.kind === "quote") {
      this.pendingQuote = this.mergeProposalDecisions(event.value as QuoteSnapshot);
      this.renderQuote();
    } else if (event.kind === "hold") {
      this.holdExpiresAt = String((event.value as Record<string, unknown>).expiresAt);
      this.dispatchEvent(new CustomEvent("kuroo:hold", { bubbles: true, composed: true, detail: event.value }));
    } else {
      const checkout = checkoutFromValue(event.value);
      if (checkout) {
        this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
        this.appendPaymentStatusNotice(this.pendingCheckout);
        this.renderCheckout();
        this.dispatchEvent(new CustomEvent("kuroo:checkout", { bubbles: true, composed: true, detail: this.pendingCheckout }));
        this.startCheckoutPolling();
      }
    }
  }

  private hydrateState(value: unknown): void {
    const state = objectRecord(value);
    if (!state) return;
    this.botPaused = state.botPaused === true;
    this.applyInputState();
    this.updateConfirmState();
    if (this.botPaused) this.setStatus("Una persona está atendiendo esta conversación. El concierge está pausado.", true);
    const proposal = quoteFromValue(state.pendingProposal);
    this.pendingQuote = proposal;
    this.renderQuote();
    const checkout = checkoutFromValue(state.pendingCheckout);
    if (checkout) {
      this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
      this.appendPaymentStatusNotice(this.pendingCheckout);
      this.renderCheckout();
      if (checkout.paymentId) this.startCheckoutPolling();
    }
  }

  private receive(raw: unknown): void {
    let frame: Record<string, unknown>;
    try { frame = JSON.parse(String(raw)) as Record<string, unknown>; } catch { return; }
    if (frame.type === "cf_agent_stream_resuming" && typeof frame.id === "string") {
      if (this.activeRequestId && frame.id !== this.activeRequestId) return;
      if (this.resumeAckedRequestIds.has(frame.id)) return;
      this.resumeAckedRequestIds.add(frame.id);
      this.activeRequestId = frame.id;
      this.activeAssistant = "";
      this.resumeProbeId = undefined;
      this.socket?.send(JSON.stringify({ type: "cf_agent_stream_resume_ack", id: frame.id }));
      this.setStatus("Retomando la operación…", false, true);
      return;
    }
    if (frame.type === "cf_agent_stream_resume_none") {
      if (frame.probeId && frame.probeId !== this.resumeProbeId) return;
      if (this.activeRequestId) this.resumeAckedRequestIds.delete(this.activeRequestId);
      this.resumeProbeId = undefined;
      if (this.activeRequestId) {
        this.activeRequestId = undefined;
        this.activeAssistant = "";
        this.setStatus("La operación terminó mientras se recuperaba la conexión. Revisá la propuesta y reintentá si hace falta.", true);
        this.renderMessages();
        this.renderQuote();
        this.renderCheckout();
      }
      return;
    }
    if (frame.type === "cf_agent_stream_pending" || frame.type === "cf_agent_chat_recovering") {
      if (!this.activeRequestId || !frame.id || frame.id === this.activeRequestId) this.setStatus("La operación sigue activa; esperando la respuesta…", false, true);
      return;
    }
    if (frame.type === "kuroo_concierge_status") {
      if (frame.requestId === this.activeRequestId) {
        const phase = typeof frame.phase === "string" ? frame.phase : "processing";
        const elapsed = typeof frame.elapsedMs === "number" ? Math.round(frame.elapsedMs / 1000) : 0;
        const label = phase === "checking_availability" ? "Consultando disponibilidad…" : phase === "calculating_proposal" ? "Calculando propuesta…" : phase === "creating_checkout" ? "Creando checkout seguro…" : phase === "still_working" ? `La operación sigue activa (${elapsed}s)…` : "Pensando…";
        this.setStatus(label, false, true);
      }
      return;
    }
    if (frame.type === "kuroo_concierge_state" || frame.type === "cf_agent_state") {
      this.hydrateState(frame.state ?? frame.data);
      return;
    }
    if (frame.type === "kuroo_chatwoot_human_message" && typeof frame.text === "string" && frame.text.trim()) {
      const assistant: ChatMessage = { id: randomId("human"), role: "assistant", parts: [{ type: "text", text: frame.text.trim() }] };
      this.messages.push(assistant);
      this.renderMessages();
      this.setStatus(typeof frame.senderName === "string" && frame.senderName.trim() ? `${frame.senderName} está atendiendo esta conversación.` : "Una persona está atendiendo esta conversación.");
      return;
    }
    if (frame.type === "kuroo_chatwoot_assistant_message" && typeof frame.text === "string" && frame.text.trim()) {
      const assistant: ChatMessage = { id: randomId("chatwoot-assistant"), role: "assistant", parts: [{ type: "text", text: frame.text.trim() }] };
      this.messages.push(assistant);
      this.renderMessages();
      this.setStatus("Respuesta recibida desde Chatwoot.");
      return;
    }
    if (frame.type === "cf_agent_chat_messages" && Array.isArray(frame.messages)) {
      const hydrated = frame.messages.filter((message): message is ChatMessage => Boolean(message) && typeof message === "object" && ((message as Record<string, unknown>).role === "user" || (message as Record<string, unknown>).role === "assistant"))
        .map((message) => ({ id: String(message.id ?? randomId("message")), role: message.role, parts: [{ type: "text" as const, text: textFromParts(message.parts) }] }));
      if (hydrated.length) { this.messages = hydrated; this.clearInputPlaceholder(); this.renderMessages(); }
      return;
    }
    if (frame.type !== "cf_agent_use_chat_response" || frame.id !== this.activeRequestId) return;
    if (frame.error) {
      this.setStatus(typeof frame.body === "string" ? frame.body : "No se pudo completar la consulta. Reintentá en unos segundos.", true);
      this.activeRequestId = undefined;
      this.activeAssistant = "";
      this.applyInputState();
      this.renderMessages();
      this.renderQuote();
      this.renderCheckout();
      return;
    }
    if (typeof frame.body === "string" && frame.body.trim()) {
      let chunk: unknown;
      try { chunk = JSON.parse(frame.body); } catch { chunk = undefined; }
      if (chunk && typeof chunk === "object") {
        const record = chunk as Record<string, unknown>;
        const delta = typeof record.delta === "string" ? record.delta : typeof record.textDelta === "string" ? record.textDelta : undefined;
        if (delta) this.activeAssistant += delta;
        if (record.type === "text" && typeof record.text === "string") this.activeAssistant += record.text;
        if (record.type === "start" || record.type === "text-start") this.activeAssistant = "";
        const metadata = record.type === "message-metadata" && record.messageMetadata && typeof record.messageMetadata === "object" ? record.messageMetadata as Record<string, unknown> : undefined;
        if (Array.isArray(metadata?.uiEvents)) for (const event of metadata.uiEvents) this.consumeUiEvent(event);
        if (record.type !== "message-metadata") this.consumeUiEvent(chunk);
        this.renderMessages();
      }
    }
    if (frame.done) {
      if (this.activeAssistant) {
        const assistant: ChatMessage = { id: randomId("assistant"), role: "assistant", parts: [{ type: "text", text: this.activeAssistant }] };
        this.messages.push(assistant);
        this.dispatchEvent(new CustomEvent("kuroo:message", { bubbles: true, composed: true, detail: assistant }));
      }
      this.activeAssistant = "";
      if (typeof frame.id === "string") this.resumeAckedRequestIds.delete(frame.id);
      this.activeRequestId = undefined;
      this.applyInputState();
      this.setStatus(this.holdExpiresAt ? `El hold vence a las ${new Date(this.holdExpiresAt).toLocaleTimeString(this.currentLocale())}.` : "¿Querés que busque otra opción?");
      this.renderMessages();
      this.renderQuote();
      this.renderCheckout();
    }
  }
}

export function defineKurooConcierge(): void {
  if (typeof customElements === "undefined" || typeof HTMLElement === "undefined") return;
  if (!customElements.get(elementName)) customElements.define(elementName, KurooConciergeElement);
}

defineKurooConcierge();
